import { useState } from "react";
import {
  BROWSERS,
  isChromeFamily,
  type BrowserConnectionStatus,
  type BrowserId,
  type BrowserSetupReport,
} from "@/desktop/browser-connection";
import { useBrowserConnection } from "@/desktop/use-browser-connection";
import {
  browserConnectionNeedsSetup,
  browserLastVerifiedLabel,
  useBrowserLastVerified,
} from "@/desktop/browser-connection-history";
import {
  requestBrowserSessionAction,
  useBrowserConnectionSession,
  type BrowserSessionAction,
} from "@/desktop/browser-connection-session";
import { useT, type Translator } from "@/i18n";
import { settingsBrowserMessages } from "@/i18n/messages/settings-browser";
import type { ConnectionPhase } from "./browser-connection-chip";

type BrowserT = Translator<(typeof settingsBrowserMessages)["en"]>;

function setupHelp(
  t: BrowserT,
  report: BrowserSetupReport,
  browserId: BrowserId,
): string | null {
  const browser = BROWSERS[browserId].name;
  if (
    report.stage === "launch" &&
    report.connection.reason === "browser_not_running"
  )
    return t("helpBrowserNotOpened", { browser });
  if (report.connection.reason === "no_page") return t("helpPageFailed");
  if (
    isChromeFamily(browserId) &&
    report.status === "awaiting_user" &&
    report.stage === "javascript" &&
    report.connection.reason === "javascript_disabled"
  )
    return t("helpAllowJavascript", { browser });
  switch (report.status) {
    case "accessibility_required":
      return t("helpAccessibility");
    case "automation_required":
      return t("helpAutomation");
    case "awaiting_user":
    case "modal_window":
      return t("helpApproval");
    case "browser_not_running":
      return t("helpClosedDuringSetup", { browser });
    case "browser_busy":
      return t("helpBusy");
    case "uncertain":
      return t("helpUncertain");
    case "unsupported":
      return t("helpUnsupported");
    default:
      return null;
  }
}

function connectionLabel(
  t: BrowserT,
  status: BrowserConnectionStatus | null,
): string {
  if (!status) return t("statusUnknown");
  if (status.reason === "disconnected") return t("statusDisconnected");
  if (status.javascript === "ready") return t("statusConnected");
  if (!status.installed) return t("statusNotInstalled");
  if (["consent_required", "denied"].includes(status.automation.status))
    return t("statusNeedsApproval");
  switch (status.reason) {
    case "browser_not_running":
      return t("statusBrowserClosed", {
        browser: BROWSERS[status.browser_id].name,
      });
    case "no_page":
      return t("statusPageUnavailable");
    case "browser_busy":
      return t("statusBrowserBusy");
    case "javascript_disabled":
      return t("statusJavascriptOff");
    case "launch_flag_required":
      return t("statusRestartRequired");
    case "unsupported":
      return t("statusUnavailable");
    case "probe_unavailable":
      return t("statusCheckFailed");
    default:
      return t("statusUnknown");
  }
}

export type AppleEventsConnection = {
  applies: boolean;
  phase: ConnectionPhase;
  /** The last page check found it ready and access still stands: the only
   * "Connected". */
  connected: boolean;
  /** Connected now, or verified before: Reconnect and Disconnect apply. */
  hasAccess: boolean;
  /** The status words: "Connected", "Connecting…", "Safari is closed". */
  label: string;
  /** "Last verified …" while not connected now, for the status tooltip. */
  verified: string | null;
  /** Not connected or needs you: what a click runs, and its words. */
  connect: { action: BrowserSessionAction; label: string };
  /** Working: whether Stop reaches it (a disconnect cannot be stopped). */
  stoppable: boolean;
  /** Unavailable: why. */
  unavailable: string | null;
  /** For the chip's tooltip: what a click does, or why not. */
  does: string;
  /** What a click on the chip runs, unless it opens the connected menu. */
  activate?: () => void;
  /** An error, or help for what the person does next. */
  message: { text: string; alert: boolean } | null;
  /** The last `run` could not reach the owner window: nothing runs. */
  requestError: string | null;
  run: (action: BrowserSessionAction) => void;
};

/**
 * The Apple Events connection of one browser: executable approval and
 * progress belong to the owner (`browser-connection-session`); this reads
 * the passive and managed evidence into one phase, its words and its next
 * action. Not `active`, it checks nothing: a caller that shows it only for
 * one choice of several keeps it quiet otherwise. Only `setup` (the browser
 * the person chose, while its setup shows) checks the page in the browser on
 * its own; otherwise only a click does (`useBrowserConnection`).
 */
export function useAppleEventsConnection(
  browserId: BrowserId,
  active = true,
  setup = false,
): AppleEventsConnection {
  const t = useT(settingsBrowserMessages);
  const session = useBrowserConnectionSession((state) => state.session);
  const managed = session?.browserId === browserId ? session : null;
  const passive = useBrowserConnection(
    browserId,
    active && !session?.busy,
    managed?.busy ? null : managed?.status,
    active && setup,
  );
  const [requestError, setRequestError] = useState<string | null>(null);
  const status =
    managed?.busy || managed?.status?.reason === "disconnected"
      ? managed.status
      : passive.status;
  const disconnected = status?.reason === "disconnected";
  const attempt = managed?.attempt?.report;
  const busy = managed?.busy ?? passive.busy;
  const anotherBusy = !!session?.busy && !managed;
  const ready = !managed?.busy && status?.javascript === "ready";
  const lastVerified = useBrowserLastVerified(browserId);
  const unresolvedAttempt =
    !!attempt &&
    !ready &&
    !["configured", "cancelled"].includes(attempt.status) &&
    (lastVerified === null || managed!.attempt!.completedAt > lastVerified);
  // Only a ready page check is "Connected"; history explains an unchecked browser.
  const connected = !disconnected && ready;
  const verified =
    !connected &&
    !disconnected &&
    lastVerified !== null &&
    (!status || !browserConnectionNeedsSetup(status))
      ? browserLastVerifiedLabel(lastVerified)
      : null;
  const completedJavascriptHelp =
    isChromeFamily(browserId) &&
    status?.javascript === "ready" &&
    (managed?.errorKind === "setting-unidentified" ||
      managed?.errorKind === "setting-not-found");
  const error =
    requestError ||
    (ready || completedJavascriptHelp ? null : managed?.error) ||
    (connected || verified ? null : passive.error);
  const restartDia =
    browserId === "dia" && status?.reason === "launch_flag_required";
  const failedRestart =
    browserId === "dia" &&
    unresolvedAttempt &&
    attempt?.stage === "restart" &&
    !ready;
  const needsHelp =
    unresolvedAttempt &&
    !managed?.error &&
    (!status ||
      status.reason === "not_checked" ||
      status.reason === attempt.connection.reason) &&
    !ready &&
    !restartDia &&
    !failedRestart &&
    attempt.stage !== "browser_window" &&
    [
      "automation_required",
      "accessibility_required",
      "awaiting_user",
      "modal_window",
    ].includes(attempt.status);
  const pageHelp =
    status?.reason === "no_page"
      ? attempt
        ? t("helpPageFailed")
        : t("helpPageAutomatic")
      : null;
  const help =
    !busy && failedRestart
      ? t("helpDiaRestartFailed")
      : !busy && verified && unresolvedAttempt
        ? setupHelp(t, attempt, browserId)
        : !busy && !connected && !verified
          ? restartDia
            ? t("helpDiaRestart")
            : (pageHelp ??
              (unresolvedAttempt &&
              (!status || status.reason === attempt.connection.reason)
                ? setupHelp(t, attempt, browserId)
                : null))
          : managed?.busy
            ? managed.guideLabel
            : null;
  const run = (action: BrowserSessionAction) => {
    setRequestError(null);
    void requestBrowserSessionAction(
      action,
      browserId,
      action === "guide" || action === "stop" ? managed?.id : undefined,
    ).catch(() => {
      setRequestError(t("errorSessionUnavailable"));
    });
  };
  const notInstalled = status?.installed === false;
  const connect: AppleEventsConnection["connect"] & { does: string } = needsHelp
    ? { action: "guide", label: t("actionGuideMe"), does: t("doesGuideMe") }
    : restartDia
      ? {
          action: "restart-connect",
          label: t("actionRestartDia"),
          does: t("doesRestartDia"),
        }
      : // A manual Disconnect is undone by checking access again.
        {
          action: disconnected ? "check" : "connect",
          label: t("actionConnect"),
          does: t("doesConnect"),
        };
  const stoppable = !!managed?.busy && managed.busy !== "disconnect";
  const unavailable = anotherBusy
    ? t("whyAnotherConnecting")
    : notInstalled
      ? t("whyNotInstalled", { browser: BROWSERS[browserId].name })
      : null;
  const phase: ConnectionPhase = managed?.busy
    ? "working"
    : anotherBusy
      ? "unavailable"
      : connected || verified
        ? "connected"
        : notInstalled
          ? "unavailable"
          : needsHelp || restartDia
            ? "needs_you"
            : "not_connected";
  return {
    applies: passive.applies,
    phase,
    connected,
    hasAccess: connected || !!verified,
    label:
      managed?.busy === "disconnect"
        ? t("statusDisconnecting")
        : managed?.busy && managed.busy !== "check"
          ? t("statusConnecting")
          : connected
            ? t("statusConnected")
            : busy || (!status && !passive.error)
              ? t("statusChecking")
              : status?.reason === "not_checked" && verified
                ? verified
                : connectionLabel(t, status),
    verified,
    connect,
    stoppable,
    unavailable,
    does:
      phase === "connected"
        ? t("doesConnected")
        : phase === "working"
          ? stoppable
            ? t("doesStop")
            : t("doesDisconnecting")
          : (unavailable ?? connect.does),
    activate:
      phase === "working"
        ? stoppable
          ? () => run("stop")
          : undefined
        : phase === "unavailable"
          ? undefined
          : () => run(connect.action),
    message: error || help ? { text: (error || help)!, alert: !!error } : null,
    requestError,
    run,
  };
}
